import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { ImmerReducer, useImmerReducer } from 'use-immer';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import {
  CoreUserGroups,
  Countries,
  Groups,
  Organisation,
  ownerScopes,
  Routes,
  Scopes,
  userGroupScopes as getUserGroupScopes
} from '@simplyanvil/cl-types';
import find from 'lodash-es/find';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { removeNullValues } from '@/libs/utils';
import { grantsAccess, resolveEffectiveScopes } from '@/libs/permissions/resolveEffectiveScopes';
import { GET_ORGANISATION } from '@/graphql/queries';
import { CREATE_ORGANISATION, UPDATE_ORGANISATION } from '@/graphql/mutations';
import { GraphqlUtils, GraphqlUtilsInterface } from '@/libs/graphqlUtils';
import { AuthStateContext, useAuthDispatch } from '@/contexts/AuthContext';
import { useBugsnag } from '@/contexts/BugsnagContext';

export interface UserOrganisation extends Organisation {
  orgId?: string;
  org: {
    defaultRoute?: Routes;
    legalEntity?: {
      address?: {
        country?: Countries;
      };
    };
    plan?: string;
  };
  access: { tokenIds: string[]; scopes: string[]; sourceIps: string[]; groups: string[] };
}

export type OrganisationStateContextType = {
  loading: boolean;
  error: string;
  currentOrganisation: Organisation;
  organisations: UserOrganisation[];
  status?: 'Ready' | 'Onboarding' | 'Creating' | 'Updating';
  // Set when an organisation failed to resolve (bad URL, deleted org, unauthorised
  // access). Consumers throw OrganisationNotFoundError during render when this is
  // true, so the nearest ErrorBoundary shows a fallback instead of routing to /404/.
  organisationNotFound?: boolean;
  notFoundOrgId?: string | null;
};

type OrganisationContextProps = {
  children: React.ReactNode;
};

export type OrganisationDispatchContextType = {
  setOrganisation: (organisation: Organisation, route?: boolean) => void;
  setOrganisationById: (orgId: string) => void;
  updateOrganisation: (organisation: Partial<Organisation>) => Promise<Organisation>;
  createOrganisation: (organisation: Partial<Organisation>) => Promise<Organisation>;
  dispatch: (action: any) => void;
  getAccessScopes: () => Scopes[];
  hasAccess: (scope: Scopes) => boolean;
  hasGroup: (group: CoreUserGroups) => boolean;
  hasSubscriptionPlan: (plan?: 'Free' | 'Enterprise' | 'Enterprise Plus') => boolean;
  clearSession: () => void;
};

// Parse the orgId out of /dashboard/<id>/..., /authentication/<id>/..., /users/<id>/...
// routes. Decodes percent-encoded segments and strips any stray query/hash that
// shouldn't appear in asPath but might from third-party redirects.
// Thrown when a requested organisation cannot be resolved (stale URL, deleted
// org, or admin accessing a non-existent id). Consumers throw this during
// render (see the `organisationNotFound` state flag) so the nearest
// ErrorBoundary shows a fallback; the BugsnagProvider filters it by
// errorClass to keep reporting quiet.
export class OrganisationNotFoundError extends Error {
  constructor(message = 'Organisation not found') {
    super(message);
    this.name = 'OrganisationNotFoundError';
  }
}

// Thrown when an organisation fetch fails because the Cognito session itself
// is no longer valid (expired/revoked/signed out elsewhere), as opposed to
// the organisation genuinely not existing. Callers should leave recovery to
// the sign-out flow (triggered by the Apollo authLink) rather than treating
// it as a missing org and surfacing OrganisationNotFoundError.
export class OrganisationAuthError extends Error {
  constructor(message = 'Not authenticated') {
    super(message);
    this.name = 'OrganisationAuthError';
  }
}

const isAuthError = (error: any): boolean => {
  const networkStatus = error?.networkError?.statusCode;
  if (networkStatus === 401 || networkStatus === 403) {
    return true;
  }

  const codes = [error?.networkError?.result?.errors, error?.graphQLErrors]
    .flat()
    .filter(Boolean)
    .map((e: any) => `${e?.extensions?.code || ''} ${e?.errorType || ''} ${e?.message || ''}`.toLowerCase());

  const messages = [error?.message?.toLowerCase(), ...codes].filter(Boolean);

  return messages.some(
    (message) =>
      message.includes('unauthorized') ||
      message.includes('unauthenticated') ||
      message.includes('not authorized') ||
      message.includes('forbidden')
  );
};

export const parseUrlOrg = (path: string): string => {
  const raw = path.split('/').length > 1 ? path.split('/')[2] : '';
  return raw ? decodeURIComponent(raw.split('?')[0].split('#')[0]) : '';
};

export const isDashboardPath = (path: string): boolean =>
  path.indexOf('dashboard/') >= 0 || path.indexOf('authentication/') >= 0 || path.indexOf('users/') >= 0;

// Fields where an explicit null is the only way to clear the stored value: the
// update API reads an omitted key as "not editing" and merges the old value back.
// removeNullValues() strips every null from the payload, so those keys have to be
// put back before the mutation goes out. Dot-separated paths reach into nested objects.
const CLEARABLE_FIELDS = [
  'parentId',
  'hun.vatCode',
  'hun.contact.name',
  'hun.contact.phone',
  'hun.contact.email',
  'hun.bankAccount.number',
  'hun.vatGroup.taxId',
  'hun.vatGroup.vatCode',
  'hun.vatGroup.country',
  'hun.auth.username',
  'hun.auth.password',
  'hun.auth.xmlSignatureKey',
  'hun.auth.xmlExchangeKey'
];

const getAtPath = (obj: Record<string, any>, path: string): any =>
  path.split('.').reduce((acc, key) => (acc === null || acc === undefined ? undefined : acc[key]), obj);

const setAtPath = <T extends Record<string, any>>(obj: T, path: string, value: any): T => {
  const keys = path.split('.');
  const result: any = { ...obj };
  let cursor = result;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
    } else {
      cursor[key] = { ...(cursor[key] || {}) };
      cursor = cursor[key];
    }
  });
  return result;
};

export const keepClearedFields = <T extends Record<string, any>>(cleaned: T, original: Record<string, any>): T =>
  CLEARABLE_FIELDS.reduce(
    (memo, field) => (getAtPath(original, field) === null ? setAtPath(memo, field, null) : memo),
    { ...cleaned }
  );

const sanitizeOrg = (organisation: Organisation): Organisation => {
  const tempOrganisation: Organisation = { ...organisation };
  // removeNullValues(tempOrganisation);
  // Map india
  if (organisation?.ind && typeof organisation.ind === 'string') {
    tempOrganisation.ind = JSON.parse(organisation.ind);
  }
  // Map india
  if (organisation?.rou && typeof organisation.rou === 'string') {
    tempOrganisation.rou = JSON.parse(organisation.rou);
  }

  return tempOrganisation;
};
const organisationReducer: ImmerReducer<OrganisationStateContextType, OrganisationStateContextType> = (
  draft: any,
  action: any
) => {
  switch (action.type) {
    case 'clearSession': {
      draft.loading = true;
      draft.error = null;
      draft.organisations = null;
      draft.currentOrganisation = null;
      draft.isAuthorisedForCurrentOrganisation = false;
      draft.status = null;

      localStorage.removeItem('org');
      break;
    }
    case 'anonymousSession': {
      draft.loading = false;
      draft.error = null;
      draft.organisations = null;
      draft.currentOrganisation = null;
      draft.status = null;
      break;
    }
    case 'init': {
      draft.currentOrganisation = sanitizeOrg(action.organisation);
      draft.organisations = action.organisations;
      draft.loading = false;
      draft.status = 'Ready';
      draft.organisationNotFound = false;
      draft.notFoundOrgId = null;
      break;
    }
    case 'onboarding': {
      draft.currentOrganisation = null;
      draft.organisations = [];
      draft.loading = false;
      draft.status = 'Onboarding';
      break;
    }
    case 'setOrganisationById': {
      draft.currentOrganisation = sanitizeOrg(action.organisation);
      draft.loading = false;
      draft.organisationNotFound = false;
      draft.notFoundOrgId = null;
      break;
    }
    case 'organisationNotFound': {
      draft.organisationNotFound = true;
      draft.notFoundOrgId = action.orgId ?? null;
      draft.loading = false;
      break;
    }
    case 'clearOrganisationNotFound': {
      draft.organisationNotFound = false;
      draft.notFoundOrgId = null;
      break;
    }
    case 'gettingOrganisation':
    case 'gettingOrganisations':
    case 'creatingOrganisation':
    case 'settingOrganisation':
    case 'updatingOrganisation': {
      draft.error = null;
      draft.loading = true;

      if (action.status) {
        draft.status = action.status;
      }
      break;
    }
    case 'error':
    case 'createOrganisationError':
    case 'updateOrganisationError': {
      draft.error = action.error;
      draft.loading = false;
      break;
    }
    case 'fetchOrganisation': {
      draft.selectedOrgId = action.organisation.id; // this is id and updateOrganisation not orgId
      draft.error = null;
      draft.loading = false;
      break;
    }
    case 'setOrganisation': {
      draft.currentOrganisation = sanitizeOrg(action.organisation);
      draft.error = null;
      draft.loading = false;
      draft.organisationNotFound = false;
      draft.notFoundOrgId = null;
      break;
    }
    // case 'setOrganisations': {
    //   draft.organisations = action.organisations;
    //   draft.error = null;
    //   draft.loading = false;
    //   break;
    // }
    case 'createdOrganisation': {
      draft.organisations = [
        { ...action.organisation, orgId: action.organisation.id, org: action.organisation },
        ...draft.organisations
      ];
      draft.currentOrganisation = sanitizeOrg(action.organisation);
      draft.error = null;
      draft.loading = false;
      draft.status = 'Ready';

      break;
    }
    case 'updatedOrganisation': {
      draft.error = null;
      draft.loading = false;
      draft.currentOrganisation = sanitizeOrg(action.organisation);
      draft.organisations = draft?.organisations?.map((org) => {
        if (org.orgId === action.organisation.id) {
          return { ...org, name: action.organisation.name };
        }
        return org;
      });
      break;
    }

    default: {
      throw new Error(`Unhandled dispatch: ${action.type}`);
    }
  }
};
const initialState = {
  currentOrganisation: undefined,
  organisations: undefined,
  loading: true,
  error: undefined,
  status: undefined,
  organisationNotFound: false,
  notFoundOrgId: null
};

export const OrganisationStateContext = createContext({} as OrganisationStateContextType);
export const OrganisationDispatchContext = createContext({} as OrganisationDispatchContextType);

const gqlUtils: GraphqlUtilsInterface = GraphqlUtils();

export const OrganisationProvider = ({ children }: OrganisationContextProps): any => {
  const router = useRouter();
  const { setBugsnagOrganisation } = useBugsnag();
  const [state, dispatch] = useImmerReducer<any, any>(organisationReducer, initialState);
  const { organisations, currentOrganisation, status, loading } = state;
  const { isAuthenticated, isLoading, user, organisations: userOrgs } = useContext(AuthStateContext);
  const { dispatch: authDispatch } = useAuthDispatch();
  const [getOrganisationQ] = useLazyQuery(GET_ORGANISATION, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only'
  });
  const [createOrganisationQ] = useMutation(CREATE_ORGANISATION, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only'
  });
  const [updateOrganisationQ] = useMutation(UPDATE_ORGANISATION);

  const setOrganisation = useCallback(
    async (organisation: Organisation, route?: boolean): Promise<void> => {
      dispatch({ type: 'setOrganisation', organisation });

      localStorage.setItem('org', organisation.id);

      // Bugsnag
      setBugsnagOrganisation(organisation.id, organisation.name);

      if (route) {
        router.push(`/dashboard/${organisation.id}/`);
      }
    },
    [dispatch]
  );

  const createOrganisation = useCallback(
    async (newOrganisation: Organisation): Promise<Organisation> => {
      if (!isAuthenticated) {
        throw new Error('Not authorized');
      }
      dispatch({ type: 'creatingOrganisation', status: 'Creating' });

      const resp = await createOrganisationQ({
        variables: { input: newOrganisation }
      });

      // Infer user org permissions for newly created org.
      const access = { scopes: ownerScopes, tokenIds: [], sourceIps: [], groups: [Groups.OWNER] };
      authDispatch({ type: 'createdNewOrg', organisation: { ...resp?.data?.createOrganisation, access } });
      dispatch({ type: 'createdOrganisation', organisation: { ...resp?.data?.createOrganisation, access } });

      setOrganisation({ ...resp?.data?.createOrganisation, access }, true);
      return resp?.data?.createOrganisation;
    },
    [dispatch, isAuthenticated, createOrganisationQ]
  );
  const updateOrganisation = useCallback(
    async (newOrganisation: Organisation): Promise<Organisation> => {
      if (!isAuthenticated) {
        throw new Error('Not authorized');
      }

      const cleanedOrganisation = keepClearedFields(
        gqlUtils.prepGQLObject(removeNullValues(newOrganisation)),
        newOrganisation
      );
      delete cleanedOrganisation?.access?.groups;
      // Groups not part of update mutation.
      const resp = await updateOrganisationQ({
        variables: { params: cleanedOrganisation, orgId: cleanedOrganisation.id }
      });

      dispatch({
        type: 'updatedOrganisation',
        organisation: resp?.data?.updateOrganisation
      });

      return resp?.data.updateOrganisation;
    },
    [dispatch, isAuthenticated, updateOrganisationQ]
  );
  const setOrganisationById = useCallback(
    async (orgId: string): Promise<void> => {
      // Syncs in-memory org state + localStorage only — does not navigate.
      // Callers that need to change the URL must use router.push themselves;
      // Phase 1 of the provider effect will then pick up the URL change and
      // call back into this function. Membership enforcement lives in that
      // effect (callers bypass via backend authoriser), so we don't gate by org list here.
      dispatch({ type: 'settingOrganisation' });

      try {
        // Fetch current org details
        const { data, error } = await getOrganisationQ({
          variables: {
            id: orgId
          }
        });

        if (isAuthError(error)) {
          throw new OrganisationAuthError();
        }

        if (!data?.organisation) {
          throw new OrganisationNotFoundError();
        }

        dispatch({ type: 'setOrganisationById', organisation: data.organisation });
        localStorage.setItem('org', orgId);

        // Bugsnag
        setBugsnagOrganisation(orgId, data.organisation.name);
      } catch (err) {
        console.error('Error fetching organisation by ID:', err);
        // Dispatch error state to prevent infinite loops
        dispatch({ type: 'error', error: err.message || 'Failed to fetch organisation' });
        // Re-throw error so calling code can handle it
        throw err;
      }
    },
    [dispatch]
  );

  // Get all access scopes for a user and org combination.
  const getAccessScopes = useCallback(
    (): Scopes[] =>
      resolveEffectiveScopes({
        currentOrganisation,
        organisations,
        user
      }),
    [currentOrganisation, organisations, user]
  );

  // TODO: Correctly cache results, as it currently repeats.
  const hasAccess = useCallback((scope: Scopes): boolean => grantsAccess(getAccessScopes(), scope), [getAccessScopes]);

  const hasGroup = (group: CoreUserGroups): boolean => user?.access?.groups?.includes(group);

  // Check whether an org has a certain subscription plan.
  const hasSubscriptionPlan = (plan: 'Free' | 'Enterprise' | 'Enterprise Plus' = 'Enterprise'): boolean => {
    // Default to Free tier when not set.
    if (!currentOrganisation?.plan) {
      return plan === 'Free';
    }

    return currentOrganisation.plan === plan || currentOrganisation.plan === 'Enterprise Plus';
  };

  // Tracks the last urlOrg we attempted to sync to. Lets us detect the
  // (rare) case where the backend returns an org with a different canonical id
  // than the one we asked for, so we don't loop forever re-fetching it.
  const lastSyncedUrlOrgRef = useRef<string | null>(null);

  // Organisations init + URL sync
  //
  // Phase 1 runs on every URL change once bootstrap is done. It keeps
  // currentOrganisation in sync with /dashboard/<id>/ URL changes (browser
  // back, paste, dropdown). Bootstrap (Phase 2) owns the first load and the
  // /dashboard/new/ onboarding case — do not collapse the two phases or the
  // post-bootstrap URL-sync bug returns.
  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      if (loading) {
        dispatch({ type: 'anonymousSession' });
      }
      return;
    }

    if (!userOrgs) {
      return;
    }

    // Support for access groups
    const normalizedOrgs = (userOrgs || organisations || []).map((org) => {
      const groupedScopes = flatten(org?.access?.groups?.map((group) => getUserGroupScopes[group]));
      return {
        ...org,
        id: org.id || org.orgId,
        orgId: org.id || org.orgId,
        access: {
          scopes: uniq([...(org?.access?.scopes || []), ...groupedScopes])
        }
      };
    });
    const path = router.asPath;
    const isDashboard = path.indexOf('dashboard') >= 0;
    const onDashboardPath = isDashboardPath(path);
    const urlOrg = parseUrlOrg(path);
    // Organisation doesn't exist (or the user isn't authorised for it). Record
    // it in state rather than navigating to /404/ — consumers (e.g. DashboardContent)
    // throw OrganisationNotFoundError during render when this is set, so the
    // nearest ErrorBoundary shows a fallback in place instead of silently
    // bouncing to another org in the user's list.
    const markOrganisationNotFound = (failedOrgId?: string) =>
      dispatch({ type: 'organisationNotFound', orgId: failedOrgId });

    // ── PHASE 1: URL-driven sync (every URL change after bootstrap) ──
    if (status === 'Ready' && urlOrg && urlOrg !== 'new' && onDashboardPath) {
      // Don't pile a second fetch on top of one already in flight.
      if (loading) return;
      // Already in sync — nothing to do. Clear the canonicalization guard so a
      // later URL change back to this org isn't blocked by stale ref state.
      if (urlOrg === currentOrganisation?.id) {
        lastSyncedUrlOrgRef.current = null;
        return;
      }

      // Backend canonicalization guard: if we already attempted this urlOrg
      // and the resulting currentOrganisation.id differs, show 404 rather
      // than retry forever.
      if (lastSyncedUrlOrgRef.current === urlOrg && currentOrganisation?.id !== urlOrg) {
        markOrganisationNotFound(urlOrg);
        return;
      }
      lastSyncedUrlOrgRef.current = urlOrg;

      const matchedOrg = find(normalizedOrgs, { id: urlOrg });
      if (matchedOrg) {
        // User is a member — switch silently, keep URL as-is.
        setOrganisationById(urlOrg).catch((err) => {
          // Session died mid-request — the authLink is already forcing a
          // sign-out; don't mask that with an organisation-not-found error.
          if (err instanceof OrganisationAuthError) return;
          markOrganisationNotFound(urlOrg);
        });
        return;
      }

      // Not in the user's direct org list. Optimistically attempt the fetch and
      // let the backend authoriser decide. This covers platform admins
      // (USR_EEZI_ADMIN), parent-org owners reaching a sub-org via the
      // Enterprise Organisations tab, and any other backend-granted access.
      // Unauthorised callers surface OrganisationNotFoundError via the rejection handler.
      setOrganisationById(urlOrg).catch((err) => {
        if (err instanceof OrganisationAuthError) return;
        markOrganisationNotFound(urlOrg);
      });
      return;
    }

    // After bootstrap is done, no further work for this effect run.
    if (status === 'Ready') {
      return;
    }

    // ── PHASE 2: Bootstrap (existing first-load logic) ──

    // Handle bootstrap access for non-direct-member callers.
    if (urlOrg && urlOrg !== 'new') {
      const matchedOrg = find(normalizedOrgs, { id: urlOrg });

      if (onDashboardPath) {
        // URL org is not in the user's direct list — could be a platform
        // admin (USR_EEZI_ADMIN), a parent-org owner reaching a sub-org,
        // or any other backend-granted access. Optimistically fetch and
        // let the backend authoriser decide. We cannot fall through to
        // the non-admin bootstrap below because `defaultOrg` would resolve
        // to one of the user's own orgs. Fetch the URL org and dispatch
        // `init` directly so both `currentOrganisation` AND `organisations`
        // (for the dropdown) are populated in a single pass.
        if (!matchedOrg) {
          if (currentOrganisation?.id !== urlOrg && lastSyncedUrlOrgRef.current !== urlOrg) {
            lastSyncedUrlOrgRef.current = urlOrg;
            dispatch({ type: 'gettingOrganisation' });
            getOrganisationQ({ variables: { id: urlOrg } })
              .then(({ data, error }) => {
                if (isAuthError(error)) {
                  // Session died mid-request — let the authLink-triggered sign-out
                  // take over instead of surfacing an organisation-not-found error.
                  return;
                }
                if (!data?.organisation) {
                  markOrganisationNotFound(urlOrg);
                  return;
                }
                dispatch({ type: 'init', organisation: data.organisation, organisations: normalizedOrgs });
                localStorage.setItem('org', urlOrg);
                setBugsnagOrganisation(urlOrg, data.organisation.name);
              })
              .catch((err) => {
                console.error('Bootstrap access to org failed:', err);
                markOrganisationNotFound(urlOrg);
              });
          }
          return;
        }
      }
    }

    // Set default org from previous session is available.
    let defaultOrg = normalizedOrgs[0];
    const prevOrg = localStorage.getItem('org');

    // Load previous org
    if (prevOrg) {
      // Ensure there is a match on current users list
      const matchedOrg = find(normalizedOrgs, { orgId: prevOrg });
      if (matchedOrg) {
        defaultOrg = matchedOrg as unknown as UserOrganisation;
      }
    }

    // Load org from URL path
    if (urlOrg && urlOrg !== 'new') {
      // Ensure there is a match on current users list
      const matchedOrg = find(normalizedOrgs, { id: urlOrg });
      if (matchedOrg) {
        defaultOrg = matchedOrg as unknown as UserOrganisation;
      }
    }

    // New account: Not on create org form
    if (!defaultOrg?.orgId && urlOrg !== 'new' && path.indexOf('dashboard') >= 0) {
      // Prevent infinite loop by checking if already redirected.
      router.push({
        pathname: '/dashboard/new/'
      });

      return;
    }

    // New account: On create org form
    if (!defaultOrg?.orgId && urlOrg === 'new' && status !== 'Creating') {
      dispatch({ type: 'onboarding' });
      return;
    }

    // Fetch current org details
    if (currentOrganisation?.id !== defaultOrg?.orgId) {
      getOrganisationQ({
        variables: {
          id: defaultOrg?.orgId
        }
      })
        .then(({ data }) => {
          if (!data?.organisation) {
            markOrganisationNotFound(defaultOrg?.orgId);
            return;
          }
          // Set default or from users list
          dispatch({ type: 'init', organisation: data.organisation, organisations: normalizedOrgs });
          localStorage.setItem('org', defaultOrg?.orgId);

          // Bugsnag
          setBugsnagOrganisation(defaultOrg?.orgId, defaultOrg?.name);

          // Route to default org
          if (isDashboard && path === '/dashboard/' && defaultOrg?.orgId) {
            router.push({
              pathname: `/dashboard/${defaultOrg?.orgId}/`
            });
          }
        })
        .catch((err) => {
          console.error(err.message, err.description);
          markOrganisationNotFound(defaultOrg?.orgId);
        });
      // return;
    }

    // Load org from path
    if (currentOrganisation && urlOrg !== defaultOrg?.orgId) {
      // setOrganisationById(urlOrg || defaultOrg.orgId); // TODO: Add back in if it doesn't create infinite loops
    }
  }, [
    isAuthenticated,
    isLoading,
    userOrgs,
    currentOrganisation,
    router.asPath,
    user?.access,
    status,
    loading,
    setOrganisationById
  ]);

  const clearSession = () => {
    dispatch({ type: 'clearSession' });

    // Bugsnag
    setBugsnagOrganisation(null, null);
  };

  const OrganisationContextValue = useMemo(
    () => ({
      setOrganisation,
      setOrganisationById,
      createOrganisation,
      updateOrganisation,
      getAccessScopes,
      hasAccess,
      hasGroup,
      hasSubscriptionPlan,
      clearSession,
      dispatch
    }),
    [
      createOrganisation,
      updateOrganisation,
      setOrganisation,
      setOrganisationById,
      getAccessScopes,
      hasAccess,
      hasGroup,
      dispatch
    ]
  );

  return (
    <OrganisationDispatchContext.Provider value={OrganisationContextValue}>
      <OrganisationStateContext.Provider value={state}>{children}</OrganisationStateContext.Provider>
    </OrganisationDispatchContext.Provider>
  );
};

export const useOrganisationState = () => {
  const c = useContext(OrganisationStateContext);
  if (!c) throw new Error('Cannot use useOrganisationState when not under the AuthProvider');
  return c;
};

export const useOrganisationDispatch = () => {
  const c = useContext(OrganisationDispatchContext);
  if (!c) throw new Error('Cannot use useAuthDispatch when not under the AuthProvider');
  return c;
};
